/**
 * Auth service — one function per AMLiq Authentication API endpoint.
 *
 * Each returns the unwrapped `data` payload and throws {@link ApiError} on
 * failure. The flow narrative:
 *
 * - First-time:  checkEmail → verifyOtp → startMfaSetup → confirmMfaSetup
 * - Returning:   checkEmail → verifyMfa (or verifyBackupCode)
 * - Social:      signInWith{Google,Microsoft} → complete session (no MFA step)
 */
import { apiRequest } from './client'
import type {
  AuthResult,
  CheckEmailResult,
  CheckProvidersResult,
  MfaBackupVerifyRequest,
  MfaSetupConfirmResult,
  MfaSetupRequiredResult,
  MfaSetupResult,
  MfaVerifyRequest,
  SocialSignInResult,
  UserProfile,
  VerifyOtpRequest,
} from './types'
import { MFA_LOGIN_IS_MANDATORY } from '@/config/app.config'

/** `GET /auth/me` — the authenticated user's profile. */
export function getCurrentUser(token: string, signal?: AbortSignal): Promise<UserProfile> {
  return apiRequest<UserProfile>('/auth/me', { token, signal })
}

/**
 * `POST /auth/check-email` — the single entry point for all sign-in attempts.
 *
 * - `{ emailVerified: false }` → an OTP was emailed; go to email verification.
 * - `{ emailVerified: true, mfaToken }` → go straight to the authenticator TOTP.
 *
 * @throws {ApiError} 400 when no account exists for the email.
 */
export function checkEmail(email: string, signal?: AbortSignal): Promise<CheckEmailResult> {
  return apiRequest<CheckEmailResult>('/auth/check-email', {
    method: 'POST',
    body: { email },
    signal,
  })
}

/**
 * `POST /auth/check-providers` — look up an email's linked providers and the
 * account-wide login methods enabled by the tenant admin.
 *
 * A pure lookup (no email sent, no session started). The login screen uses the
 * returned `loginMethods` to hide/block options the admin has turned off;
 * enforcement is UI-side, as the sign-in endpoints do not reject a disabled
 * method.
 *
 * `mfaLoginEnabled` is normalized to `true` while email + authenticator code is
 * mandatory (AMLIQ-264) — the endpoint is unchanged, so a tenant that turned it
 * off before would otherwise be locked out of email sign-in with no way to
 * re-enable it. See {@link MFA_LOGIN_IS_MANDATORY}.
 */
export async function checkProviders(
  email: string,
  signal?: AbortSignal,
): Promise<CheckProvidersResult> {
  const result = await apiRequest<CheckProvidersResult>('/auth/check-providers', {
    method: 'POST',
    body: { email },
    signal,
  })
  if (!MFA_LOGIN_IS_MANDATORY) return result
  return { ...result, loginMethods: { ...result.loginMethods, mfaLoginEnabled: true } }
}

/**
 * `POST /auth/otp/verify` — verify the email OTP (first-time users only).
 * Returns a JWT (use as the bearer token for MFA setup) and `mfaSetupRequired`.
 */
export function verifyOtp(
  request: VerifyOtpRequest,
  signal?: AbortSignal,
): Promise<MfaSetupRequiredResult> {
  return apiRequest<MfaSetupRequiredResult>('/auth/otp/verify', {
    method: 'POST',
    body: request,
    signal,
  })
}

/**
 * `POST /auth/google` — sign in with a Google ID token.
 *
 * (`POST /auth/apple` exists in the API but the product offers no Apple
 * sign-in, so no client is provided for it.)
 */
export function signInWithGoogle(
  idToken: string,
  signal?: AbortSignal,
): Promise<SocialSignInResult> {
  return apiRequest<SocialSignInResult>('/auth/google', {
    method: 'POST',
    body: { idToken },
    signal,
  })
}

/**
 * `POST /auth/microsoft` — sign in with a Microsoft (Entra ID) ID token.
 *
 * Mirrors {@link signInWithGoogle} and returns the same social-sign-in shape.
 */
export function signInWithMicrosoft(
  idToken: string,
  signal?: AbortSignal,
): Promise<SocialSignInResult> {
  return apiRequest<SocialSignInResult>('/auth/microsoft', {
    method: 'POST',
    body: { idToken },
    signal,
  })
}

/**
 * `POST /auth/mfa/setup` — generate a TOTP secret + QR code.
 * Requires the JWT returned by {@link verifyOtp}.
 */
export function startMfaSetup(token: string, signal?: AbortSignal): Promise<MfaSetupResult> {
  return apiRequest<MfaSetupResult>('/auth/mfa/setup', {
    method: 'POST',
    token,
    signal,
  })
}

/**
 * `POST /auth/mfa/setup/confirm` — confirm one TOTP code to activate MFA.
 * Requires the JWT returned by {@link verifyOtp}. Returns one-time backup codes.
 */
export function confirmMfaSetup(
  code: string,
  token: string,
  signal?: AbortSignal,
): Promise<MfaSetupConfirmResult> {
  return apiRequest<MfaSetupConfirmResult>('/auth/mfa/setup/confirm', {
    method: 'POST',
    body: { code },
    token,
    signal,
  })
}

/**
 * `POST /auth/mfa/verify` — complete sign-in with the TOTP code and the
 * single-use `mfaToken` from {@link checkEmail}.
 */
export function verifyMfa(request: MfaVerifyRequest, signal?: AbortSignal): Promise<AuthResult> {
  return apiRequest<AuthResult>('/auth/mfa/verify', {
    method: 'POST',
    body: request,
    signal,
  })
}

/**
 * `POST /auth/mfa/backup-verify` — complete sign-in with a one-time backup code
 * instead of the authenticator app.
 */
export function verifyBackupCode(
  request: MfaBackupVerifyRequest,
  signal?: AbortSignal,
): Promise<AuthResult> {
  return apiRequest<AuthResult>('/auth/mfa/backup-verify', {
    method: 'POST',
    body: request,
    signal,
  })
}
